'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { TASK_SETTING_OPTION } from '@/app/constants';
import type { TaskSettings } from '@/app/types';
import { ToolSettingsWithConfig } from '../../_components/ToolSettingsWithConfig';

interface VideoRemakeSettingsProps {
  settings: Partial<TaskSettings>;
  updateSettings: (settings: Partial<TaskSettings>) => void;
}

const ASPECT_RATIO_OPTIONS = [
  { label: '1:1', value: '1:1', showVip: false },
  { label: '16:9', value: '16:9', showVip: false },
  { label: '9:16', value: '9:16', showVip: false },
  { label: '4:3', value: '4:3', showVip: false },
  { label: '3:4', value: '3:4', showVip: false },
];

const RESOLUTION_OPTIONS = [
  { label: '720p', value: '720p', showVip: false },
  { label: '1080p', value: '1080p', showVip: false },
];

const SETTING_KEY_CONFIG: TASK_SETTING_OPTION[] = [
  TASK_SETTING_OPTION.AspectRatio,
  TASK_SETTING_OPTION.Resolution,
  TASK_SETTING_OPTION.RetainOriginalAudio,
];

export const VideoRemakeSettings: FC<VideoRemakeSettingsProps> = ({ settings, updateSettings }) => {
  const t = useTranslations();
  const aspectRatio = settings?.aspect_ratio ?? '16:9';
  const resolution = settings?.resolution ?? '1080p';
  const withAudio = settings?.retain_original_audio ?? true;

  return (
    <ToolSettingsWithConfig
      className="w-full"
      includedOptions={SETTING_KEY_CONFIG}
      showOptionDivider={false}
      optionsWrapperClassName="gap-5 md:gap-5"
      aspectRatioOption={{
        value: aspectRatio,
        options: ASPECT_RATIO_OPTIONS,
        onChange: (value) => updateSettings({ aspect_ratio: value }),
      }}
      resolutionOption={{
        value: resolution,
        options: RESOLUTION_OPTIONS,
        onChange: (value) => updateSettings({ resolution: value }),
      }}
      retainOriginalAudioOption={{
        value: withAudio,
        options: [],
        onChange: (value) => updateSettings({ retain_original_audio: value }),
        title: t('tools.videoRemake.settings.withAudio'),
      }}
    />
  );
};
